import { Component, ChangeDetectionStrategy } from '@angular/core';

import { ActivatedRoute, Router } from '@angular/router';
import { TranslocoModule, TranslocoService } from '@jsverse/transloco';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { HandleBookingService } from '../../../services/handle-booking.service';
import { TranslateAttributePipe } from '../../../../pipes/translate-attribute/translate-attribute.pipe';

@Component({
    standalone: true,
    selector: 'app-cancel-additional-services',
    imports: [
    TranslocoModule,
    MatButtonModule,
    MatCheckboxModule,
    MatProgressBarModule,
    TranslateAttributePipe
],
    templateUrl: './cancel-additional-services.component.html',
    changeDetection: ChangeDetectionStrategy.Eager,
    styleUrls: ['./cancel-additional-services.component.css']
})
export class CancelAdditionalServicesComponent {
    isLoading = false;
    email: string | null = null;
    bookingNumber: string | null = null;
    booking: any = null;
    additionalServices: any[] = [];
    selectedServiceIds: string[] = [];
    hasSpecialParams: boolean = false;

    constructor(
        private route: ActivatedRoute,
        private router: Router,
        private handleBookingService: HandleBookingService,
        private snackBar: MatSnackBar,
        private translocoService: TranslocoService
    ) { }

    public ngOnInit() {
        this.getPreservedQueryParams();
        this.route.paramMap.subscribe(params => {
            this.email = params.get('email');
            this.bookingNumber = params.get('booking_number');

            if (!this.email || !this.bookingNumber) {
                this.routerNavigatePreservingParams(['/parkifly/handle-booking']);
                return;
            }

            this.loadBooking();
        });
    }

    loadBooking() {
        this.isLoading = true;
        this.handleBookingService.getBookingDetails(this.email!, this.bookingNumber!).subscribe({
            next: bookingDetails => {
                this.booking = bookingDetails;
                this.additionalServices = bookingDetails?.additionalServices?.filter((service: any) => service.status !== 'Cancelled' && service.status !== 'Removed') || [];
                this.additionalServices = this.sortSubmissionOfKeyFirst(this.additionalServices);
                this.selectedServiceIds = this.additionalServices.map((service: any) => service.id);
                this.isLoading = false;
            },
            error: error => {
                this.isLoading = false;
                console.error('Failed to load booking details for cancel add-on page:', error);
                const message = this.translocoService.translate('FLYGETS.SELFSERVICE.VALIDATION_NOK');
                this.snackBar.open(message, 'OK', { duration: 3000, verticalPosition: 'top', horizontalPosition: 'center' });
                this.routerNavigatePreservingParams(['/parkifly/handle-booking']);
            }
        });
    }

    getUncheckedServiceIds(): string[] {
        return this.additionalServices
            .filter(service => !this.selectedServiceIds.includes(service.id))
            .map(service => service.id);
    }

    getUncheckedServicesAmount(): number {
        return this.additionalServices
            .filter(service => !this.selectedServiceIds.includes(service.id))
            .reduce((total, service) => {
                const amount = Number(service.amount) || 0;
                const vatAmount = Number(service.vatAmount) || 0;
                return total + amount + vatAmount;
            }, 0);
    }

    get hasDeletionTargets(): boolean {
        return this.getUncheckedServiceIds().length > 0;
    }

    resetSelections(): void {
        this.selectedServiceIds = this.additionalServices.map((service: any) => service.id);
    }

    cancelServices() {
        const uncheckedIds = this.getUncheckedServiceIds();
        if (!this.email || !this.bookingNumber || uncheckedIds.length === 0) {
            return;
        }

        this.isLoading = true;
        this.handleBookingService.cancelAdditionalServices(this.bookingNumber, this.email, uncheckedIds)
            .then(() => {
                this.isLoading = false;
                const message = this.translocoService.translate('HANDLE_BOOKING.CANCEL_SERVICES') || 'Additional services canceled';
                this.snackBar.open(message, 'OK', { duration: 3000, verticalPosition: 'top', horizontalPosition: 'center' });
                const prefix = this.router.url.startsWith('/parkifly-new') ? 'parkifly-new' : 'parkifly';
                this.routerNavigatePreservingParams([`/${prefix}/booking-overview`, { email: this.email, booking_number: this.bookingNumber }]);
            })
            .catch(error => {
                this.isLoading = false;
                console.error('Failed to cancel additional services:', error);
                const message = this.translocoService.translate('PARKIFLY.MANAGE_BOOKING.CANCEL_SERVICES_FAILED') || 'Could not cancel additional services';
                this.snackBar.open(message, 'OK', { duration: 3000, verticalPosition: 'top', horizontalPosition: 'center' });
            });
    }

    trackByServiceId(index: number, item: any) {
        return item?.id || index;
    }

    parseServiceNameEn(service: any): string {
        try {
            const nameObject = typeof service.name === 'string' ? JSON.parse(service.name) : service.name;
            return nameObject?.en || '';
        } catch {
            return service.name?.en || '';
        }
    }

    isServiceSelected(service: any): boolean {
        return this.selectedServiceIds.includes(service.id);
    }

    isSubmissionOfKey(service: any): boolean {
        return this.parseServiceNameEn(service) === 'Submission of key';
    }

    hasOtherSelectedServices(): boolean {
        return this.selectedServiceIds.some(id => {
            const service = this.additionalServices.find((s: any) => s.id === id);
            return service && !this.isSubmissionOfKey(service);
        });
    }

    isServiceDisabled(service: any): boolean {
        return this.isSubmissionOfKey(service) && this.hasOtherSelectedServices();
    }

    sortSubmissionOfKeyFirst(services: any[]): any[] {
        return [...services].sort((a, b) => {
            const aIsSubmission = this.isSubmissionOfKey(a) ? 0 : 1;
            const bIsSubmission = this.isSubmissionOfKey(b) ? 0 : 1;
            return aIsSubmission - bIsSubmission;
        });
    }

    onServiceToggle(service: any, event: any): void {
        const serviceId = service.id;
        const isSubmissionOfKey = this.isSubmissionOfKey(service);
        const isChecked = event.checked;

        if (!isChecked && isSubmissionOfKey && this.hasOtherSelectedServices()) {
            event.source.checked = true;
            return;
        }

        if (isChecked) {
            if (!this.selectedServiceIds.includes(serviceId)) {
                this.selectedServiceIds.push(serviceId);
            }

            // If checking a non-submission service and submission of key is not selected, auto-select it
            if (!isSubmissionOfKey) {
                const submissionOfKeyService = this.additionalServices.find(s => this.isSubmissionOfKey(s));
                if (submissionOfKeyService && !this.selectedServiceIds.includes(submissionOfKeyService.id)) {
                    this.selectedServiceIds.push(submissionOfKeyService.id);
                }
            }
        } else {
            this.selectedServiceIds = this.selectedServiceIds.filter(id => id !== serviceId);
        }
    }

    getServiceName(nameJson: any): string {
        try {
            const parsed = typeof nameJson === 'string' ? JSON.parse(nameJson) : nameJson;
            return parsed[this.translocoService.getActiveLang()] || parsed['en'] || nameJson;
        } catch {
            return nameJson;
        }
    }

    goBack() {
        if (this.email && this.bookingNumber) {
            const prefix = this.router.url.startsWith('/parkifly-new') ? 'parkifly-new' : 'parkifly';
            this.routerNavigatePreservingParams([`/${prefix}/edit-parking-additional-services`, { email: this.email, booking_number: this.bookingNumber }]);
        } else {
            this.routerNavigatePreservingParams(['/parkifly/handle-booking']);
        }
    }

    private getPreservedQueryParams(): Record<string, string> {
        const params = this.route.snapshot.queryParamMap;
        const queryParams: Record<string, string> = {};
        const lang = params.get('lang');
        const mobile = params.get('mobile');

        if (lang) {
            queryParams.lang = lang;
        }
        if (mobile) {
            queryParams.mobile = mobile;
        }

        this.hasSpecialParams = !!(lang || mobile);

        return queryParams;
    }

    private routerNavigatePreservingParams(commands: any[], extras: Record<string, any> = {}) {
        const preserved = this.getPreservedQueryParams();
        const mergedQueryParams = {
            ...(extras.queryParams || {}),
            ...preserved
        };

        if (Object.keys(mergedQueryParams).length > 0) {
            extras = { ...extras, queryParams: mergedQueryParams };
        }

        this.router.navigate(commands, extras);
    }
}
